import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { feesApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TID } from "@/lib/testIds";
import { Check, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending");
  const navigate = useNavigate();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let stop = false;
    const tick = async () => {
      if (stop) return;
      attempts.current += 1;
      try {
        const r = await feesApi.paymentStatus(sessionId);
        if (r.payment_status === "paid") { setStatus("paid"); return; }
        if (r.payment_status === "failed" || r.payment_status === "expired") { setStatus("failed"); return; }
      } catch (err) { console.debug("[PaymentSuccess] transient poll error", err); }
      if (attempts.current > 20) { setStatus("timeout"); return; }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#090A0F] text-slate-100 px-6">
      <div className="max-w-md w-full aq-card p-8 text-center">
        {status === "pending" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-emerald-400" />
            <h1 className="mt-4 font-display text-2xl font-bold">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-slate-400">Stripe is finalising your subscription. This normally takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 grid place-items-center">
              <Check className="w-7 h-7 text-emerald-400" />
            </div>
            <h1 data-testid={TID.paymentSuccessTitle} className="mt-4 font-display text-3xl font-extrabold text-emerald-300">Fees settled</h1>
            <p className="mt-2 text-sm text-slate-400">Your platform fees are paid in full. Back to hunting scalps.</p>
            <Button onClick={() => navigate("/dashboard", { replace: true })}
              className="mt-6 w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
              Go to terminal
            </Button>
          </>
        )}
        {(status === "failed" || status === "timeout" || status === "error") && (
          <>
            <h1 className="font-display text-2xl font-bold text-amber-300">Payment {status}</h1>
            <p className="mt-2 text-sm text-slate-400">We could not confirm your payment yet. If Stripe already charged you, your subscription will unlock momentarily.</p>
            <Button onClick={() => navigate("/dashboard", { replace: true })}
              className="mt-6 w-full h-11 rounded-full bg-white/10 hover:bg-white/20 text-white">
              Back to dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
